// @ts-nocheck
// TODO: Fix type
import React, { useEffect, useState } from "react";

import get from "lodash/get";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormControl, Grid, IconButton } from "@mui/material";

import { Close as CloseIcon } from "@/assets/icons";
import { AddButton, FieldError, PhoneNumberInput } from "@/components/atoms";
import TextInput from "@/components/atoms/Form/TextInput/TextInput";
import { RefSelectInput } from "@/components/molecules";
import { Path } from "@/core/fieldPath";

import { CommunicationBaseFormType } from "./formType";

const availablePhones = 3;
const availableEmails = 2;

type CommunicatioInputNames =
    | "phone0"
    | "phone1"
    | "phone2"
    | "typePhone0"
    | "typePhone1"
    | "typePhone2"
    | "email0"
    | "email1";

export type CommunicationInputNamesType<CommunicationFormType = CommunicationBaseFormType> = {
    [K in CommunicatioInputNames]?: Path<CommunicationFormType>;
};

const inputNamesDefault: CommunicationInputNamesType = {
    phone0: "phone0",
    phone1: "phone1",
    phone2: "phone2",
    typePhone0: "typePhone0",
    typePhone1: "typePhone1",
    typePhone2: "typePhone2",
    email0: "email0",
    email1: "email1",
};

type CommunicationProps<CommunicationFormType> = {
    required?: boolean;
    singleEmail?: boolean;
    inputNames?: CommunicationInputNamesType<CommunicationFormType>;
};

const Communication = <CommunicationFormType = CommunicationBaseFormType,>({
    required = false,
    singleEmail = false,
    inputNames = inputNamesDefault,
}: CommunicationProps<CommunicationFormType>): React.ReactElement => {
    const {
        formState: { errors },
        setValue,
        watch,
        control,
    } = useFormContext<CommunicationFormType>();
    const { t } = useTranslation();

    const [displayedPhones, setDisplayedPhones] = useState(1);
    const [displayedEmails, setDisplayedEmails] = useState(1);

    const email1 = watch(inputNames.email1);
    const phone1 = watch(inputNames.phone1);
    const phone2 = watch(inputNames.phone2);
    const typePhone2 = watch(inputNames.typePhone2);

    useEffect(() => {
        if (email1) setDisplayedEmails(2);
    }, [email1]);

    useEffect(() => {
        if (phone2) {
            setDisplayedPhones(3);
        } else if (phone1) {
            setDisplayedPhones(2);
        }
    }, [phone1]);

    useEffect(() => {
        if (phone2) setDisplayedPhones(3);
    }, [phone2]);

    const handleAddOneMorePhone = () => {
        setDisplayedPhones(displayedPhones + 1);
    };

    const handleAddOneMoreEmail = () => {
        setDisplayedEmails(displayedEmails + 1);
    };

    const removePhone1 = () => {
        if (displayedPhones === 3) {
            setValue(inputNames.phone1, phone2);
            setValue(inputNames.typePhone1, typePhone2);
        } else {
            setValue(inputNames.phone1, "", { shouldValidate: false });
            setValue(inputNames.typePhone1, "", { shouldValidate: false });
        }
        setDisplayedPhones(displayedPhones - 1);
    };

    const removePhone2 = () => {
        setValue(inputNames.phone2, "", { shouldValidate: false });
        setValue(inputNames.typePhone2, "", { shouldValidate: false });
        setDisplayedPhones(displayedPhones - 1);
    };

    const removeEmail1 = () => {
        setValue(inputNames.email1, "", { shouldValidate: false });
        setDisplayedEmails(displayedEmails - 1);
    };

    return (
        <Grid container>
            <Grid item xs={12}>
                <FormControl error={!!get(errors, inputNames.phone0)}>
                    <Grid container alignItems="self-end" columnSpacing={1}>
                        <Grid item xs={3}>
                            <RefSelectInput
                                label={t("Common.Phone")}
                                required={required}
                                name={inputNames.typePhone0}
                                control={control}
                                hideErrorMessage
                                withEmptyOption
                                table="phoneNumberType"
                            />
                        </Grid>
                        <Grid item xs={9}>
                            <PhoneNumberInput name={inputNames.phone0} control={control} hideErrorMessage />
                        </Grid>
                    </Grid>
                    <FieldError error={get(errors, inputNames.phone0)} />
                </FormControl>
            </Grid>
            <Grid item xs={12} style={{ display: displayedPhones <= 1 && "none" }}>
                <FormControl error={!!get(errors, inputNames.phone1)}>
                    <Grid container alignItems="self-end" columnSpacing={1}>
                        <Grid item xs={3}>
                            <RefSelectInput
                                name={inputNames.typePhone1}
                                hideErrorMessage
                                label=""
                                control={control}
                                withEmptyOption
                                table="phoneNumberType"
                            />
                        </Grid>
                        <Grid item xs={8}>
                            <PhoneNumberInput name={inputNames.phone1} control={control} hideErrorMessage />
                        </Grid>
                        <Grid item xs={1} alignSelf="center">
                            <IconButton onClick={removePhone1} aria-label={t("Common.RemovePhone")}>
                                <CloseIcon />
                            </IconButton>
                        </Grid>
                    </Grid>
                    <FieldError error={get(errors, inputNames.phone1)} />
                </FormControl>
            </Grid>
            <Grid item xs={12} style={{ display: displayedPhones <= 2 && "none" }}>
                <FormControl error={!!get(errors, inputNames.phone2)}>
                    <Grid container alignItems="self-end" columnSpacing={1}>
                        <Grid item xs={3}>
                            <RefSelectInput
                                name={inputNames.typePhone2}
                                hideErrorMessage
                                label=""
                                control={control}
                                withEmptyOption
                                table="phoneNumberType"
                            />
                        </Grid>
                        <Grid item xs={8}>
                            <PhoneNumberInput name={inputNames.phone2} control={control} hideErrorMessage />
                        </Grid>
                        <Grid item xs={1} alignSelf="center">
                            <IconButton onClick={removePhone2} aria-label={t("Common.RemovePhone")}>
                                <CloseIcon />
                            </IconButton>
                        </Grid>
                    </Grid>
                    <FieldError error={get(errors, inputNames.phone2)} />
                </FormControl>
            </Grid>
            <Grid item xs={12}>
                <AddButton
                    disabled={displayedPhones === availablePhones}
                    type="button"
                    data-testid="phone-button"
                    label={t("Common.OneMorePhone")}
                    onClick={handleAddOneMorePhone}
                />
            </Grid>
            <Grid item xs={12}>
                <TextInput
                    name={inputNames.email0}
                    label={t("Common.Email")}
                    required={required}
                    autoComplete="off"
                    control={control}
                />
            </Grid>
            {!singleEmail && (
                <>
                    <Grid
                        item
                        container
                        xs={12}
                        style={{ display: displayedEmails <= 1 && "none" }}
                        alignItems="self-end"
                        columnSpacing={1}
                    >
                        <Grid item xs={11}>
                            <TextInput name={inputNames.email1} autoComplete="off" control={control} />
                        </Grid>
                        <Grid item xs={1} alignSelf="center">
                            <IconButton onClick={removeEmail1} aria-label={t("Common.RemoveEmail")}>
                                <CloseIcon />
                            </IconButton>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <AddButton
                            disabled={displayedEmails === availableEmails}
                            data-testid="email-button"
                            style={{ marginTop: "10px" }}
                            label={t("Common.OneMoreEmail")}
                            onClick={handleAddOneMoreEmail}
                        />
                    </Grid>
                </>
            )}
        </Grid>
    );
};

export default Communication;
